import { Shield, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function PrivacyPage() {
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="privacy-page">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/" className="text-slate-400 hover:text-white p-2 -ml-2 rounded-lg hover:bg-slate-800 transition-colors" data-testid="privacy-back-btn">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex items-center gap-2">
          <Shield className="text-emerald-400" size={22} />
          <h1 className="text-2xl font-bold text-white">Privacy Policy</h1>
        </div>
      </div>

      <p className="text-slate-400 text-sm">Last updated: June 9, 2026</p>

      <div className="space-y-5 text-slate-300 text-sm leading-relaxed">
        <Section title="1. Overview">
          Apna Premier League ("APL", "we", "our") is a free multi-sport fantasy platform
          covering fantasy cricket (including the IPL) and fantasy football (Football World Cup 2026).
          This policy explains how we collect, use, and protect your information when you
          use our mobile application and website.
        </Section>

        <Section title="2. Information We Collect">
          <ul className="list-disc pl-5 space-y-1.5 mt-2">
            <li><strong className="text-white">Account Information:</strong> When you sign up via Firebase Authentication, we collect your email address, display name, and profile photo (if provided via Google Sign-In).</li>
            <li><strong className="text-white">Profile Details:</strong> To personalise leaderboards and improve our service, we may collect your country, city, year of birth, gender, and your favourite team. Providing these is optional where indicated.</li>
            <li><strong className="text-white">Tournament Registration:</strong> We record which sport-specific tournaments you register for (e.g. cricket and/or football) and your squads, captains, predictions, and points for each.</li>
            <li><strong className="text-white">Device Information:</strong> We collect your device's push notification token (FCM token) to send you match updates and alerts. This is stored securely and used solely for notification delivery.</li>
            <li><strong className="text-white">Advertising Data:</strong> Our app uses Google AdMob to display advertisements. AdMob may collect device identifiers (such as Advertising ID) and usage data to serve personalised or contextual ads. You can opt out of personalised ads in your device settings.</li>
            <li><strong className="text-white">Usage Data:</strong> We collect data about your in-app activity, including teams created, predictions made, daily rewards, referrals, and leaderboard participation, across both cricket and football.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-disc pl-5 space-y-1.5 mt-2">
            <li>To create and manage your account across cricket and football</li>
            <li>To provide each fantasy sport experience (teams, scores, leaderboards, predictions)</li>
            <li>To personalise leaderboards and aggregate, anonymised audience insights (e.g. by country, city, age range)</li>
            <li>To send push notifications about match updates and results</li>
            <li>To display advertisements via Google AdMob</li>
            <li>To improve and maintain the application</li>
          </ul>
        </Section>

        <Section title="4. Data Storage">
          Your data is stored in MongoDB, a secure cloud database. We use industry-standard
          security practices including encrypted connections, access controls, and regular
          backups. We do not sell your personal information to third parties.
        </Section>

        <Section title="5. Third-Party Services">
          <ul className="list-disc pl-5 space-y-1.5 mt-2">
            <li><strong className="text-white">Firebase (Google):</strong> Authentication and user management. <a href="https://firebase.google.com/support/privacy" className="text-emerald-400 hover:underline" target="_blank" rel="noopener noreferrer">Firebase Privacy Policy</a></li>
            <li><strong className="text-white">Google AdMob:</strong> Ad serving and monetisation. <a href="https://policies.google.com/privacy" className="text-emerald-400 hover:underline" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
            <li><strong className="text-white">Firebase Cloud Messaging (FCM):</strong> Push notification delivery.</li>
          </ul>
        </Section>

        <Section title="6. Your Rights">
          You may request deletion of your account and associated data at any time by
          contacting us. You can disable push notifications through your device settings.
          You can opt out of personalised advertising through your device's privacy settings.
        </Section>

        <Section title="7. Children's Privacy">
          APL is not intended for children under 13. We do not knowingly collect personal
          information from children under 13.
        </Section>

        <Section title="8. Changes to This Policy">
          We may update this privacy policy from time to time. We will notify you of any
          changes by posting the new policy on this page and updating the "Last updated" date.
        </Section>

        <Section title="9. Contact Us">
          If you have questions about this privacy policy, please contact us at{" "}
          <a href="mailto:support@apnapremierleague.com" className="text-emerald-400 hover:underline">
            support@apnapremierleague.com
          </a>.
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
      <h2 className="text-base font-semibold text-white mb-2">{title}</h2>
      <div>{children}</div>
    </div>
  );
}
